/**
 * ExitSurvey — /exit-survey. The 3-question beta exit survey linked from the
 * wrap-up email: star rating, would-you-recommend, one open comment.
 * One response per tester, editable after submission.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Check, Loader, Pencil, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const REC_OPTIONS = [
  { value: "yes", label: "Yes, absolutely" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Not yet" },
];

export default function ExitSurvey() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [recommend, setRecommend] = useState("");
  const [comment, setComment] = useState("");
  const [existing, setExisting] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/exit-survey/mine").then((r) => {
      const resp = r.data.response;
      if (resp) {
        setExisting(resp);
        setRating(resp.rating || 0);
        setRecommend(resp.recommend || "");
        setComment(resp.comment || "");
      }
    }).finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (!rating) { setError("Please pick a star rating."); return; }
    if (!recommend) { setError("Please answer the recommend question."); return; }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/exit-survey", { rating, recommend, comment });
      setExisting(data.response);
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen rm-paper flex items-center justify-center px-4 py-10" data-testid="exit-survey-page">
      <div className="w-full max-w-xl">
        <div className="text-center mb-5">
          <div className="rm-tag mb-3" style={{ background: "#FFC145" }}>Beta exit survey · 3 questions</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase" style={{ color: "#0F1B2D" }}>
            {done ? "Thank you!" : "How was your beta?"}
          </h1>
          {!done && (
            <p className="text-sm mt-2" style={{ color: "#475569" }}>
              Three quick questions — your honest take shapes what Resmo becomes.
            </p>
          )}
        </div>

        <div className="rm-card-static p-5 md:p-6" style={{ background: "white" }}>
          {loading ? (
            <div className="py-10 text-center font-mono-rm text-[11px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              <Loader size={15} className="animate-spin inline mr-2" /> Loading
            </div>
          ) : done ? (
            <div className="text-center py-6" data-testid="exit-survey-done">
              <div className="inline-flex items-center justify-center rounded-full mb-4"
                   style={{ width: 64, height: 64, background: "#D1FAE5", border: "3px solid #0E5A4A" }}>
                <Check size={30} style={{ color: "#0E5A4A" }} strokeWidth={3} />
              </div>
              <div className="font-display text-xl uppercase mb-2" style={{ color: "#0F1B2D" }}>
                Feedback received.
              </div>
              <p className="text-[13px] mb-6" style={{ color: "#475569" }}>
                You rated Resmo {existing?.rating}/5 — thank you for four weeks of testing.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button onClick={() => setDone(false)} className="rm-btn rm-btn-secondary text-sm" data-testid="exit-survey-edit">
                  <Pencil size={14} /> Update my answers
                </button>
                <Link to="/" className="rm-btn rm-btn-primary text-sm" data-testid="exit-survey-home">
                  Back to Resmo <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {existing && (
                <div className="p-2.5 rounded text-[12px]"
                     style={{ background: "#EFF6FF", border: "1.5px solid #BFDBFE", color: "#1E3A5F" }}
                     data-testid="exit-survey-existing-note">
                  You already submitted on {new Date(existing.created_at).toLocaleDateString()} — feel free to update your answers.
                </div>
              )}

              <div>
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-2" style={{ color: "#FF6A00", fontWeight: 700 }}>
                  · 1 of 3 — Overall, how was Resmo?
                </div>
                <div className="flex gap-1" data-testid="exit-survey-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRating(n)}
                            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
                            data-testid={`exit-survey-star-${n}`}>
                      <Star size={34}
                            fill={(hover || rating) >= n ? "#FFC145" : "none"}
                            style={{ color: (hover || rating) >= n ? "#F59E0B" : "#CBD5E1" }} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-2" style={{ color: "#1E40AF", fontWeight: 700 }}>
                  · 2 of 3 — Would you recommend Resmo to a friend in a job hunt?
                </div>
                <div className="flex flex-wrap gap-2">
                  {REC_OPTIONS.map((o) => (
                    <button key={o.value} onClick={() => setRecommend(o.value)}
                            className="px-3 py-2 rounded-md text-[12.5px] font-mono-rm uppercase tracking-widest"
                            style={{
                              border: "2px solid #0F1B2D",
                              fontWeight: 700,
                              background: recommend === o.value ? "#0F1B2D" : "white",
                              color: recommend === o.value ? "#FFC145" : "#0F1B2D",
                              cursor: "pointer",
                            }}
                            data-testid={`exit-survey-rec-${o.value}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-2" style={{ color: "#0E5A4A", fontWeight: 700 }}>
                  · 3 of 3 — What should we hear before launch? (optional)
                </div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
                          maxLength={2000}
                          placeholder="Best moment, biggest frustration, the one thing you'd change…"
                          className="w-full rounded px-3 py-2 text-[13px]"
                          style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", resize: "vertical" }}
                          data-testid="exit-survey-comment" />
              </div>

              {error && (
                <div className="p-2.5 rounded text-[12px] font-mono-rm"
                     style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", color: "#B91C1C" }}
                     data-testid="exit-survey-error">
                  {error}
                </div>
              )}

              <button onClick={submit} disabled={busy}
                      className="rm-btn rm-btn-primary w-full py-3 text-sm disabled:opacity-50"
                      data-testid="exit-survey-submit">
                {busy ? <Loader size={15} className="animate-spin" /> : <Check size={15} />}
                {busy ? "Sending…" : existing ? "Update my feedback" : "Send my feedback"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
